import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getCustomer, updateCustomer, getProjects, createProject, deleteProject } from '../services/api';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Plus,
  FolderOpen,
  FileText,
  Edit,
  Trash2,
  Loader2,
  Building2,
  Mail,
  Calendar
} from 'lucide-react';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    integration_type: '',
    tags: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    contact_email: '',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [customerRes, projectsRes] = await Promise.all([
        getCustomer(id),
        getProjects(id)
      ]);
      setCustomer(customerRes.data);
      setProjects(projectsRes.data);
      setEditForm({
        name: customerRes.data.name,
        description: customerRes.data.description || '',
        contact_email: customerRes.data.contact_email || '',
        tags: customerRes.data.tags?.join(', ') || ''
      });
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = projectForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      await createProject({ ...projectForm, customer_id: id, tags });
      toast.success('Project created successfully');
      setShowProjectDialog(false);
      setProjectForm({ name: '', description: '', integration_type: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = editForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      await updateCustomer(id, { ...editForm, tags });
      toast.success('Customer updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Delete project "${projectName}" and all related documentation?`)) return;
    try {
      await deleteProject(projectId);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8">
        {/* Breadcrumb & Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/customers">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Customers
              </Button>
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-slate-600 font-medium">{customer?.name}</span>
          </div>
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-customer-btn">
                <Edit className="h-4 w-4 mr-2" />
                Edit Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Plus_Jakarta_Sans']">Edit Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateCustomer} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editForm.contact_email}
                    onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={editForm.tags}
                    onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#2B8FD8] hover:bg-[#2075b3]" disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Customer Info Card */}
        <Card className="border-slate-200 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className="p-4 bg-blue-50 rounded-xl">
                <Building2 className="h-10 w-10 text-[#2B8FD8]" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2" data-testid="customer-name">
                  {customer?.name}
                </h1>
                {customer?.description && (
                  <p className="text-slate-600 mb-4">{customer.description}</p>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  {customer?.contact_email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {customer.contact_email}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Created {new Date(customer?.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-1">
                    <FolderOpen className="h-4 w-4" />
                    {customer?.project_count || 0} projects
                  </div>
                </div>
                {customer?.tags && customer.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {customer.tags.map((tag, idx) => (
                      <Badge key={`ctag-${tag}`} variant="secondary" className="bg-slate-100 text-slate-600">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
            Projects ({projects.length})
          </h2>
          <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="add-project-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Plus_Jakarta_Sans']">Create New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Project Name *</Label>
                  <Input
                    data-testid="project-name-input"
                    placeholder="SAP B1 Integration"
                    value={projectForm.name}
                    onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    data-testid="project-description-input"
                    placeholder="Integration project description..."
                    value={projectForm.description}
                    onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Integration Type</Label>
                  <Input
                    data-testid="project-type-input"
                    placeholder="B1IF, SQL, API, etc."
                    value={projectForm.integration_type}
                    onChange={(e) => setProjectForm({ ...projectForm, integration_type: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    data-testid="project-tags-input"
                    placeholder="sap, inventory, sync"
                    value={projectForm.tags}
                    onChange={(e) => setProjectForm({ ...projectForm, tags: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowProjectDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                    disabled={saving}
                    data-testid="create-project-submit"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Create Project
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card 
                key={project.id} 
                className="border-slate-200 hover:shadow-md transition-all hover:border-blue-200 group"
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-50 rounded-lg">
                        <FolderOpen className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
                          {project.name}
                        </CardTitle>
                        {project.integration_type && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {project.integration_type}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                      onClick={(e) => { e.preventDefault(); handleDeleteProject(project.id, project.name); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{project.description}</p>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <FileText className="h-4 w-4" />
                      {project.documentation_count} docs
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {project.tags && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {project.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={`ptag-${tag}`} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Link to={`/projects/${project.id}`}>
                    <Button variant="outline" size="sm" className="w-full group-hover:border-[#2B8FD8] group-hover:text-[#2B8FD8]">
                      View Project
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">
                No projects yet
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                Create your first project to start documenting integrations for this customer
              </p>
              <Button 
                className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                onClick={() => setShowProjectDialog(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Project
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CustomerDetailPage;
